/**
 * GAM order rows of the campaign picker — used by the flat "All campaigns"
 * view and inside every advertiser group card, so both render one order the
 * same way (lifecycle pill, flight, delivered, per-campaign action).
 */

import React from 'react';
import { Button, tableStyles } from '../../ui';
import type { ReportableOrder } from '../../../services/campaignReportsApi';
import { campaignPageUrl, fmtDate, fmtInt, lifecycleOf } from './shared';
import { LifecyclePill } from './pills';

interface OrderPickerTableProps {
  orders: ReportableOrder[];
  onCreate: (order: ReportableOrder) => void;
  /** Rendered flush inside a group card (the card provides the border). */
  embedded?: boolean;
}

const OrderPickerTable: React.FC<OrderPickerTableProps> = ({ orders, onCreate, embedded = false }) => (
  <div style={embedded ? { overflowX: 'auto' } : tableStyles.wrapScroll}>
    <table style={tableStyles.table}>
      <thead>
        <tr>
          <th style={tableStyles.th}>Campaign (GAM order)</th>
          <th style={tableStyles.th}>Brand / Advertiser</th>
          <th style={tableStyles.th}>Flight</th>
          <th style={tableStyles.th}>Status</th>
          <th style={tableStyles.thNum}>Delivered</th>
          <th style={tableStyles.th} />
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => {
          const lifecycle = lifecycleOf(o);
          return (
            <tr key={o.gamOrderId}>
              <td style={{ ...tableStyles.td, fontWeight: 600 }}>
                {o.gamOrderName}
                <div style={{ fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 }}>Order {o.gamOrderId}</div>
              </td>
              <td style={tableStyles.td}>{o.advertiserName}</td>
              <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                {fmtDate(o.startDate)} →{' '}
                {o.endDate ? fmtDate(o.endDate) : <span style={{ color: 'var(--fg-4)' }}>no end date</span>}
              </td>
              <td style={tableStyles.td}>
                <LifecyclePill lifecycle={lifecycle} />
              </td>
              <td style={tableStyles.tdNum}>{fmtInt(o.totalImpressionsDelivered)}</td>
              <td style={{ ...tableStyles.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {o.existingPageSlug ? (
                  <a href={campaignPageUrl(o.existingPageSlug)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                    <Button size="sm" variant="subtle">Page exists ↗</Button>
                  </a>
                ) : lifecycle === 'in_work' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    title="Report pages need an approved GAM order — DRAFT / PENDING_APPROVAL orders can't be reported on yet"
                  >
                    Awaiting GAM approval
                  </Button>
                ) : (
                  <Button size="sm" variant="primary" onClick={() => onCreate(o)}>Create report page</Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default OrderPickerTable;
